import {
  useCallback,
  useState,
  useMemo,
  useTransition,
  useEffect,
} from "react";

import { Alert, Autocomplete, type SelectedKey } from "@stacklet/ui";
import { useFormContext, Controller } from "react-hook-form";
import { useRefetchableFragment, graphql } from "react-relay";

import { ROLES } from "app/constants/rbac";
import { createFilterElement } from "app/utils/filters";

import type { ACLPrincipalPicker_principals$key } from "./__generated__/ACLPrincipalPicker_principals.graphql";
import type { ACLPrincipalPickerPrincipalsRefetchQuery } from "./__generated__/ACLPrincipalPickerPrincipalsRefetchQuery.graphql";

export interface PrincipalOption {
  label: string;
  value: string;
  group: string;
  type: "User" | "UserGroup";
}

interface PrincipalItem extends SelectedKey {
  id: string;
  group: string;
  type: "User" | "UserGroup";
}

export type ACLTarget = "repository" | "policy-collection" | "account-group";

/** Props for ACLPrincipalPicker component */
interface ACLPrincipalPickerProps {
  /** Role name from the available role descriptions */
  role: keyof typeof ROLES;
  /** Field name for form control */
  fieldName: string;
  /** GraphQL query reference for principals */
  queryRef: ACLPrincipalPicker_principals$key;
  /** Callback when granting access to a new principal */
  onGrantAccess: (newPrincipal: PrincipalOption) => void;
  /** Callback when revoking access from a principal */
  onRevokeAccess: (principalValue: string) => void;
  /** Optional target for the input to display specific helper text */
  target?: ACLTarget;
  disabled?: boolean;
  /** Label for the input */
  label: string;
}

function toItem(option: PrincipalOption): PrincipalItem {
  return {
    id: option.value,
    name: option.label,
    group: option.group,
    type: option.type,
  };
}

function TypeBadge({ type }: { type: PrincipalOption["type"] }) {
  return (
    <span className="inline-flex items-center justify-center px-xs text-label-extra-small font-bold text-text-secondary">
      {type === "UserGroup" ? "GROUP" : "User"}
    </span>
  );
}

export function ACLPrincipalPicker({
  role,
  fieldName,
  queryRef,
  onGrantAccess,
  onRevokeAccess,
  target,
  disabled = false,
  label,
}: ACLPrincipalPickerProps) {
  const [error, setError] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  const methods = useFormContext();
  const { control, watch } = methods;

  const selectedPrincipals = watch(fieldName) || [];

  const [data, refetch] = useRefetchableFragment<
    ACLPrincipalPickerPrincipalsRefetchQuery,
    ACLPrincipalPicker_principals$key
  >(
    graphql`
      fragment ACLPrincipalPicker_principals on Query
      @argumentDefinitions(
        usersFirst: { type: "Int", defaultValue: 1000 }
        userGroupsFirst: { type: "Int", defaultValue: 1000 }
        usersFilter: { type: "FilterElementInput" }
        userGroupsFilter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "ACLPrincipalPickerPrincipalsRefetchQuery") {
        userGroups(first: $userGroupsFirst, filterElement: $userGroupsFilter) {
          edges {
            node {
              name
              roleAssignmentPrincipal
            }
          }
        }
        users(first: $usersFirst, filterElement: $usersFilter) {
          edges {
            node {
              email
              name
              roleAssignmentPrincipal
            }
          }
        }
      }
    `,
    queryRef,
  );

  const availableOptions = useMemo(() => {
    const groups = (data?.userGroups?.edges || []).map((edge) => ({
      label: edge.node.name || "",
      value: edge.node.roleAssignmentPrincipal || "",
      group: "Groups",
      type: "UserGroup" as const,
    }));

    const users = (data?.users?.edges || []).map((edge) => ({
      label: edge.node.email || edge.node.name || "",
      value: edge.node.roleAssignmentPrincipal || "",
      group: "Users",
      type: "User" as const,
    }));

    return [...groups, ...users];
  }, [data]);

  const completeOptions = useMemo(() => {
    const availableMap = new Map(
      availableOptions.map((option) => [option.value, option]),
    );

    const combinedOptions: PrincipalOption[] = [];

    availableOptions.forEach((option) => {
      combinedOptions.push(option);
    });

    // Add selected options that aren't in available options
    selectedPrincipals.forEach((principal: PrincipalOption) => {
      if (!availableMap.has(principal.value)) {
        combinedOptions.push(principal);
      }
    });

    return combinedOptions;
  }, [availableOptions, selectedPrincipals]);

  const refetchPrincipals = useCallback(
    (search: string) => {
      if (!queryRef) return;

      const value = search.trim();
      const usersFilter = createFilterElement([
        { name: "username", operator: "contains", value },
      ]);
      const userGroupsFilter = createFilterElement([
        { name: "name", operator: "starts_with", value },
      ]);

      startTransition(() => {
        refetch({
          usersFirst: 1000,
          userGroupsFirst: 1000,
          usersFilter: value ? usersFilter : undefined,
          userGroupsFilter: value ? userGroupsFilter : undefined,
        });
      });
    },
    [queryRef, refetch, startTransition],
  );

  useEffect(() => {
    // Initial fetch
    startTransition(() => {
      refetch({
        usersFirst: 1000,
        userGroupsFirst: 1000,
        usersFilter: undefined,
        userGroupsFilter: undefined,
      });
    });
  }, [refetch, startTransition]);

  const { v2Options, optionLookup } = useMemo(() => {
    return {
      v2Options: completeOptions.map(toItem),
      optionLookup: new Map(completeOptions.map((o) => [o.value, o])),
    };
  }, [completeOptions]);

  const handlePrincipalChange = useCallback(
    (
      selected: PrincipalItem[],
      fieldValue: PrincipalOption[],
      onFieldChange: (value: PrincipalOption[]) => void,
    ) => {
      setError(null);

      const newPrincipals = selected
        .map((item) => optionLookup.get(item.id))
        .filter((p): p is PrincipalOption => p !== undefined);

      newPrincipals
        .filter((p) => !fieldValue.some((f) => f.value === p.value))
        .forEach((p) => onGrantAccess(p));

      fieldValue
        .filter((f) => !newPrincipals.some((p) => p.value === f.value))
        .forEach((f) => onRevokeAccess(f.value));

      onFieldChange(newPrincipals);
    },
    [optionLookup, onGrantAccess, onRevokeAccess],
  );

  return (
    <div className="flex flex-col gap-xs">
      {error && (
        <Alert className="mb-xs" severity="error">
          {error}
        </Alert>
      )}

      <Controller
        control={control}
        name={fieldName}
        render={({ field }) => {
          const fieldValue = (field.value || []) as PrincipalOption[];

          return (
            <Autocomplete<PrincipalItem>
              assistiveText={
                target
                  ? ROLES[role]?.description[target]
                  : ROLES[role]?.description.short
              }
              inputRef={field.ref}
              isDisabled={disabled}
              items={v2Options}
              label={label}
              menuTrigger="focus"
              onBlur={field.onBlur}
              onChange={(next) => {
                const selected = Array.isArray(next)
                  ? next
                  : next
                    ? [next]
                    : [];
                handlePrincipalChange(selected, fieldValue, field.onChange);
              }}
              onInputChange={refetchPrincipals}
              placeholder="Search users and groups"
              tag={(item: PrincipalItem) => (
                <Autocomplete.Tag id={item.id} textValue={item.name}>
                  {item.name}
                  {item.type === "UserGroup" ? " (GROUP)" : ""}
                </Autocomplete.Tag>
              )}
              value={fieldValue.map(toItem)}
              multiple
              serverSideFilter
            >
              {(item: PrincipalItem) => (
                <Autocomplete.Option id={item.id} textValue={item.name}>
                  <span className="flex w-full items-center justify-between">
                    <span className="text-text-primary">{item.name}</span>
                    <TypeBadge type={item.type} />
                  </span>
                </Autocomplete.Option>
              )}
            </Autocomplete>
          );
        }}
      />
    </div>
  );
}
